import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, MagnifyingGlass, DoorOpen, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListRooms, adminCreateRoom, adminUpdateRoom, adminDeleteRoom,
  adminRestoreRoom, adminHardDeleteRoom,
  adminListOrgs, adminListBuildings, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, selectCls, Modal } from '../../components/admin/Field';

const ROOM_TYPES = ['private_room', 'shared_room', 'apartment', 'corridor', 'common_area'];

export default function AdminRooms() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current, isSuperAdmin } = useOrg();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [viewArchived, setViewArchived] = useState(false);

  const allowed = user?.role === 'admin';

  const reload = async () => {
    setLoading(true);
    try {
      const { items } = await adminListRooms({ archived: viewArchived });
      const filtered = viewArchived ? items.filter((r) => r.deleted_at) : items;
      setItems(filtered);
    } catch (err) {
      toast.error(formatAdminError('List rooms', err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    reload();
    adminListBuildings({ limit: 200 }).then(({ items }) => setBuildings(items)).catch(() => setBuildings([]));
    if (isSuperAdmin) {
      adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, isSuperAdmin, viewArchived]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((r) => String(r.room_name || '').toLowerCase().includes(needle));
  }, [items, q]);

  const onSave = async (form) => {
    setBusy(true);
    try {
      if (editing === 'new') {
        await adminCreateRoom(form);
        toast.success(t('admin_room_created'));
      } else {
        await adminUpdateRoom(editing.id, form);
        toast.success(t('admin_room_updated'));
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError(editing === 'new' ? 'Create room' : 'Update room', err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteRoom(confirm.id);
      toast.success(t('admin_room_deleted'));
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Delete room', err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (r) => {
    setBusy(true);
    try {
      await adminRestoreRoom(r.id);
      toast.success(t('admin_restored'));
      reload();
    } catch (err) {
      toast.error(formatAdminError('Restore room', err));
    } finally {
      setBusy(false);
    }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteRoom(hardConfirm.id);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete room', err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_rooms_title')}>
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title={t('admin_rooms_title')}
      description={isSuperAdmin ? t('admin_rooms_sub_super') : t('admin_rooms_sub_org', { org: current?.name || '—' })}
    >

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            placeholder={t('search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-rooms-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone_line bg-white text-sm focus:outline-none focus:border-sage"
          />
        </div>
        {!viewArchived && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            data-testid="admin-room-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700"
          >
            <Plus size={16} weight="bold" /> {t('admin_room_new')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex items-center mb-3">
          <ArchiveToggle value={viewArchived} onChange={setViewArchived} />
        </div>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {viewArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-rooms-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_field_name')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_type')}</th>
                <th className="px-5 py-3 font-medium">{t('room_floor')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_status')}</th>
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {filtered.map((r) => (
                <tr key={r.id} className={`hover:bg-bg-alt/40 ${viewArchived ? 'opacity-70' : ''}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <DoorOpen size={16} weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-ink truncate">{r.room_name}</div>
                        {r.corpus_name && (
                          <div className="text-[11px] text-ink-soft truncate">{r.corpus_name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{t(`room_type_${r.room_type}`) || r.room_type}</td>
                  <td className="px-5 py-3 text-ink-soft">{r.floor != null ? r.floor : '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
                      viewArchived
                        ? 'bg-terracotta-100 text-terracotta-700'
                        : (r.status === 'active' ? 'bg-sage-100 text-sage-700' : 'bg-bg-alt text-ink-muted')
                    }`}>
                      {viewArchived ? t('admin_view_archived').toLowerCase() : (r.status || 'active')}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {viewArchived ? (
                      <>
                        <button onClick={() => onRestore(r)} disabled={busy} data-testid={`admin-room-restore-${r.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 mr-1 disabled:opacity-50"
                          aria-label={t('admin_restore')} title={t('admin_restore')}>
                          <ArrowCounterClockwise size={16} />
                        </button>
                        <button onClick={() => setHardConfirm(r)} disabled={busy} data-testid={`admin-room-hard-delete-${r.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                          aria-label={t('admin_hard_delete')} title={t('admin_hard_delete')}>
                          <Warning size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing(r)} data-testid={`admin-room-edit-${r.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink mr-1" aria-label={t('edit')}>
                          <PencilSimple size={16} />
                        </button>
                        <button onClick={() => setConfirm(r)} data-testid={`admin-room-delete-${r.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700" aria-label={t('admin_delete')}>
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <RoomForm
          room={editing === 'new' ? null : editing}
          orgs={orgs}
          buildings={buildings}
          showOrgPicker={isSuperAdmin}
          activeOrg={current}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_room_delete_title')}
        description={confirm ? t('admin_room_delete_confirm', { name: confirm.room_name }) : ''}
        confirmLabel={t('admin_delete')}
        danger
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.room_name }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger
        busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function RoomForm({ room, orgs, buildings, showOrgPicker, activeOrg, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    room_name: room?.room_name || '',
    room_type: room?.room_type || 'private_room',
    floor: room?.floor != null ? String(room.floor) : '',
    status: room?.status || 'active',
    corpus_id: room?.corpus_id || '',
    organization_id: room?.organization_id || activeOrg?.id || '',
  });

  // Buildings scoped to the currently selected org (for super-admin) or all
  // visible buildings (for org-admin who only ever sees their own).
  const visibleBuildings = (buildings || []).filter((b) =>
    !form.organization_id || !b.organization_id || b.organization_id === form.organization_id
  );

  const submit = (e) => {
    e.preventDefault();
    if (!form.room_name.trim() || !form.corpus_id) return;
    const body = {
      room_name: form.room_name.trim(),
      room_type: form.room_type,
      floor: form.floor === '' ? null : Number(form.floor),
      status: form.status,
      corpus_id: form.corpus_id,
    };
    if (showOrgPicker && form.organization_id) body.organization_id = form.organization_id;
    onSave(body);
  };

  return (
    <Modal onCancel={onCancel}>
      <form
        onSubmit={submit}
        data-testid="admin-room-form"
        className="space-y-4"
      >
        <h2 className="font-heading text-2xl text-ink">
          {room ? t('admin_room_edit_title') : t('admin_room_new_title')}
        </h2>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_name')} required>
            <input type="text" required value={form.room_name}
              onChange={(e) => setForm({ ...form, room_name: e.target.value })}
              data-testid="admin-room-name"
              className={inputCls} />
          </Field>
          <Field label={t('room_floor')}>
            <input type="number" value={form.floor}
              onChange={(e) => setForm({ ...form, floor: e.target.value })}
              data-testid="admin-room-floor"
              className={inputCls} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_type')}>
            <select value={form.room_type}
              onChange={(e) => setForm({ ...form, room_type: e.target.value })}
              data-testid="admin-room-type"
              className={selectCls}>
              {ROOM_TYPES.map((rt) => <option key={rt} value={rt}>{t(`room_type_${rt}`)}</option>)}
            </select>
          </Field>
          <Field label={t('admin_field_status')}>
            <select value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="admin-room-status"
              className={selectCls}>
              <option value="active">{t('status_active') || 'active'}</option>
              <option value="inactive">{t('status_inactive') || 'inactive'}</option>
            </select>
          </Field>
        </div>

        {showOrgPicker && orgs.length > 0 && (
          <Field label={t('admin_field_organization')}>
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value, corpus_id: '' })}
              data-testid="admin-room-org"
              className={selectCls}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
        )}

        <Field label={t('admin_field_building')} hint={t('admin_field_building_required')} required>
          <select value={form.corpus_id}
            onChange={(e) => setForm({ ...form, corpus_id: e.target.value })}
            data-testid="admin-room-corpus"
            className={selectCls}>
            <option value="">—</option>
            {visibleBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="admin-room-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.room_name.trim() || !form.corpus_id}
            data-testid="admin-room-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
